<template>
  <div class="min-h-screen bg-gray-50">

    <!-- ═══════════════════════════════════════════════════════════════════════
         Modale Recalcul : Confirmation → En cours → Terminé
    ═══════════════════════════════════════════════════════════════════════ -->
    <transition name="modal-fade">
      <div
        v-if="recalcModal.open"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        style="background: rgba(0,0,0,0.5);"
        @click.self="closeRecalcModalIfDone"
      >
        <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">

          <!-- En-tête -->
          <div class="px-6 pt-6 pb-4 border-b border-gray-100 flex items-center gap-3">
            <div :class="[
              'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center',
              recalcModal.phase === 'confirm' ? 'bg-amber-100' :
              recalcModal.phase === 'running' ? 'bg-blue-100' :
              recalcModal.phase === 'done' && recalcModal.errorCount === 0 ? 'bg-green-100' : 'bg-orange-100'
            ]">
              <i :class="[
                'text-lg',
                recalcModal.phase === 'confirm'  ? 'fas fa-calculator text-amber-600' :
                recalcModal.phase === 'running'  ? 'fas fa-cog fa-spin text-blue-600' :
                recalcModal.phase === 'done' && recalcModal.errorCount === 0 ? 'fas fa-check text-green-600' :
                'fas fa-exclamation-triangle text-orange-600'
              ]"></i>
            </div>
            <h2 class="text-lg font-bold text-gray-900">
              <template v-if="recalcModal.phase === 'confirm'">Recalculer toutes les dates</template>
              <template v-else-if="recalcModal.phase === 'running'">Recalcul en cours…</template>
              <template v-else-if="recalcModal.errorCount === 0">✅ Recalcul terminé</template>
              <template v-else>⚠ Recalcul terminé avec erreurs</template>
            </h2>
          </div>

          <!-- Corps -->
          <div class="px-6 py-5">

            <!-- Phase confirmation -->
            <div v-if="recalcModal.phase === 'confirm'" class="text-sm text-gray-600 space-y-2">
              <p>
                Cette action va recalculer la <strong>date de prochaine visite</strong> pour
                <strong>l'ensemble des lots</strong> via la ScheduledVisitChain.
              </p>
              <p class="text-gray-500">
                Le traitement se fait par batch de {{ recalcModal.batchSize }} lots.
                La progression sera affichée en temps réel.
              </p>
            </div>

            <!-- Phase running ou done : barre de progression -->
            <template v-else>
              <!-- Barre de progression -->
              <div class="mb-4">
                <div class="flex justify-between text-xs text-gray-500 mb-1">
                  <span>{{ recalcModal.processedTotal.toLocaleString('fr-FR') }} / {{ recalcModal.grandTotal > 0 ? recalcModal.grandTotal.toLocaleString('fr-FR') : '…' }} lots traités</span>
                  <span class="font-semibold" :class="recalcModal.phase === 'done' ? 'text-green-700' : 'text-blue-700'">
                    {{ progressPercent }}%
                  </span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    class="h-3 rounded-full transition-all duration-300 ease-out"
                    :class="recalcModal.phase === 'done' ? 'bg-green-500' : 'bg-blue-500'"
                    :style="{ width: progressPercent + '%' }"
                  ></div>
                </div>
              </div>

              <!-- Statistiques -->
              <div class="flex gap-6 text-sm">
                <div class="flex items-center gap-1.5">
                  <span class="w-2.5 h-2.5 rounded-full bg-green-400 flex-shrink-0"></span>
                  <span class="text-gray-600">
                    <span class="font-semibold text-gray-800">{{ recalcModal.changedTotal.toLocaleString('fr-FR') }}</span>
                    date(s) modifiée(s)
                  </span>
                </div>
                <div class="flex items-center gap-1.5">
                  <span class="w-2.5 h-2.5 rounded-full bg-red-400 flex-shrink-0"></span>
                  <span class="text-gray-600">
                    <span class="font-semibold text-gray-800">{{ recalcModal.errorCount.toLocaleString('fr-FR') }}</span>
                    erreur(s)
                  </span>
                </div>
              </div>

              <!-- Batch en cours (phase running) -->
              <p v-if="recalcModal.phase === 'running'" class="mt-3 text-xs text-gray-400 italic">
                Page {{ recalcModal.currentPage }} en cours de traitement…
              </p>

              <!-- Message de fin (phase done) -->
              <p v-if="recalcModal.phase === 'done'" class="mt-3 text-sm font-medium" :class="recalcModal.errorCount === 0 ? 'text-green-700' : 'text-orange-700'">
                {{ recalcModal.errorCount === 0
                  ? `Tous les lots ont été recalculés avec succès. Le tableau va être rafraîchi.`
                  : `Recalcul terminé. ${recalcModal.errorCount} lot(s) ont rencontré une erreur.`
                }}
              </p>

              <!-- Détail des erreurs (collapsible) -->
              <details v-if="recalcModal.phase === 'done' && recalcModal.errors.length > 0" class="mt-3">
                <summary class="cursor-pointer text-xs text-red-500 hover:text-red-700 select-none font-medium">
                  Voir les erreurs ({{ recalcModal.errors.length }})
                </summary>
                <div class="mt-2 max-h-40 overflow-y-auto bg-red-50 rounded-md p-3 space-y-1">
                  <div
                    v-for="err in recalcModal.errors"
                    :key="err.id"
                    class="text-xs text-red-700"
                  >
                    <span class="font-semibold">{{ err.nsil || err.id }}</span> — {{ err.message }}
                  </div>
                </div>
              </details>
            </template>
          </div>

          <!-- Pied -->
          <div class="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
            <!-- Phase confirmation -->
            <template v-if="recalcModal.phase === 'confirm'">
              <button
                @click="closeRecalcModal"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none"
              >
                Annuler
              </button>
              <button
                @click="startRecalculation"
                class="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 focus:outline-none inline-flex items-center gap-2"
              >
                <i class="fas fa-calculator"></i>
                Lancer le recalcul
              </button>
            </template>

            <!-- Phase running -->
            <template v-else-if="recalcModal.phase === 'running'">
              <button
                disabled
                class="px-4 py-2 text-sm font-medium text-white bg-gray-400 rounded-md cursor-not-allowed inline-flex items-center gap-2"
              >
                <i class="fas fa-spinner fa-spin"></i>
                En cours…
              </button>
            </template>

            <!-- Phase done -->
            <template v-else>
              <button
                @click="closeRecalcModalAndRefresh"
                class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none inline-flex items-center gap-2"
              >
                <i class="fas fa-sync-alt"></i>
                Fermer et rafraîchir
              </button>
            </template>
          </div>

        </div>
      </div>
    </transition>

    <!-- Header -->
    <div class="bg-white shadow">
      <div class="w-full px-4 sm:px-6 lg:px-8 py-6">
        <div class="flex flex-wrap items-center justify-between gap-4">
          <div class="flex items-center gap-3">
            <i class="fas fa-calendar-check text-indigo-500 text-3xl"></i>
            <div>
              <h1 class="text-2xl font-bold text-gray-900">Dernières visites</h1>
              <p class="text-sm text-gray-500 mt-1">
                Tous les lots triés par date de dernière visite (la plus récente en premier)
              </p>
            </div>
          </div>
          <div class="flex flex-wrap items-center gap-3">
            <!-- Recherche NSIL -->
            <div class="relative">
              <span class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i class="fas fa-search text-gray-400 text-xs"></i>
              </span>
              <input
                v-model="nsilSearch"
                type="text"
                placeholder="Rechercher par NSIL…"
                class="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 w-52"
              />
              <button
                v-if="nsilSearch"
                @click="nsilSearch = ''"
                class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <i class="fas fa-times text-xs"></i>
              </button>
            </div>
            <!-- Filtre mandataire -->
            <div class="relative">
              <span class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i class="fas fa-building text-gray-400 text-xs"></i>
              </span>
              <input
                v-model="mandatorSearch"
                type="text"
                placeholder="Filtrer par mandataire…"
                class="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 w-52"
              />
              <button
                v-if="mandatorSearch"
                @click="mandatorSearch = ''"
                class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <i class="fas fa-times text-xs"></i>
              </button>
            </div>
            <!-- Badge compteur -->
            <span v-if="totalCount > 0" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800">
              {{ visits.length }} / {{ totalCount }} visite(s)
            </span>
            <!-- Bouton recalcul lots incorrects -->
            <button
              @click="openRecalcModal"
              :disabled="recalcModal.open || loading"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-amber-600 hover:bg-amber-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              title="Recalculer la date de prochaine visite pour tous les lots"
            >
              <i class="fas fa-calculator mr-2"></i>
              ⚙ Recalculer tout
            </button>
            <!-- Bouton réinitialiser -->
            <button
              @click="resetAndLoad"
              :disabled="loading || recalcModal.phase === 'running'"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i :class="loading ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'" class="mr-2"></i>
              {{ loading ? 'Chargement…' : 'Réinitialiser' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="w-full px-4 sm:px-6 lg:px-8 py-8">

      <!-- Flash message -->
      <transition name="fade">
        <div v-if="flashMessage" :class="flashClass" class="mb-6 rounded-md p-4 flex items-start">
          <i :class="flashIconClass" class="text-xl flex-shrink-0 mt-0.5 mr-3"></i>
          <span class="text-sm font-medium">{{ flashMessage }}</span>
          <button @click="flashMessage = null" class="ml-auto text-current opacity-60 hover:opacity-100">✕</button>
        </div>
      </transition>

      <!-- Loading initial (aucune visite encore chargée) -->
      <div v-if="loading && visits.length === 0" class="text-center py-16">
        <i class="fas fa-spinner fa-spin text-indigo-500 text-5xl mb-4"></i>
        <p class="text-gray-600">Chargement des visites…</p>
      </div>

      <!-- Erreur -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
        <div class="flex">
          <i class="fas fa-exclamation-circle text-red-400 text-xl flex-shrink-0 mt-0.5"></i>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Erreur lors du chargement</h3>
            <p class="mt-1 text-sm text-red-700">{{ error }}</p>
          </div>
        </div>
      </div>

      <!-- Tableau -->
      <div v-else class="bg-white rounded-lg shadow overflow-hidden">

        <!-- Barre de résultats -->
        <div class="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <span class="text-sm text-gray-500">
            <span class="font-semibold text-gray-700">{{ visits.length }}</span>
            <template v-if="totalCount"> / {{ totalCount }}</template>
            visite(s) affichée(s)
          </span>
          <span class="text-xs text-gray-400">
            <i class="fas fa-info-circle mr-1"></i>
            Modifier la date met à jour <code class="bg-gray-100 px-1 rounded">data_refreshed_at</code> → synchronisation PWA déclenchée
          </span>
        </div>

        <!-- Table scrollable -->
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 text-sm">
            <thead class="bg-gray-800 text-white">
              <tr>
                <th class="px-4 py-3 text-left font-semibold w-8">#</th>
                <th class="px-4 py-3 text-left font-semibold whitespace-nowrap">Lot</th>
                <th class="px-4 py-3 text-left font-semibold whitespace-nowrap">Date de visite</th>
                <th class="px-4 py-3 text-left font-semibold whitespace-nowrap">Mandataire</th>
                <th class="px-4 py-3 text-left font-semibold whitespace-nowrap">Délai</th>
                <!-- ── Nouvelles colonnes règles ── -->
                <th class="px-4 py-3 text-left font-semibold whitespace-nowrap" style="min-width:180px">Dernier occupant</th>
                <th class="px-4 py-3 text-left font-semibold whitespace-nowrap">Occupation</th>
                <th class="px-4 py-3 text-left font-semibold whitespace-nowrap" style="min-width:160px">Visitable</th>
                <th class="px-4 py-3 text-left font-semibold whitespace-nowrap">Fin gestion</th>
                <!-- ──────────────────────────────── -->
                <th class="px-4 py-3 text-left font-semibold whitespace-nowrap">Prochaine visite</th>
                <th class="px-4 py-3 text-left font-semibold" style="min-width:240px">Diagnostic / Résolution</th>
                <th class="px-4 py-3 text-left font-semibold">Action</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr
                v-for="(visit, index) in visits"
                :key="visit.id"
                class="hover:bg-gray-50 transition-colors"
              >
                <!-- # -->
                <td class="px-4 py-3 text-gray-400 text-xs">{{ index + 1 }}</td>

                <!-- Lot -->
                <td class="px-4 py-3">
                  <template v-if="visit.property">
                    <div class="font-bold text-gray-800">
                      {{ visit.property.nsil || visit.property.propertyCode || '—' }}
                    </div>
                    <div class="text-xs text-indigo-400 mt-0.5">
                      {{ formatAddress(visit.property) }}
                    </div>
                  </template>
                  <span v-else class="text-gray-400 italic">—</span>
                </td>

                <!-- Date de visite -->
                <td class="px-4 py-3 text-gray-600 whitespace-nowrap">
                  {{ formatDateTime(visit.createdAt) }}
                </td>

                <!-- Mandataire -->
                <td class="px-4 py-3">
                  <span v-if="visit.mandator" class="text-gray-700 text-xs">
                    {{ visit.mandator.name }}
                  </span>
                  <span v-else class="text-gray-400 italic text-xs">—</span>
                </td>

                <!-- Délai -->
                <td class="px-4 py-3">
                  <span
                    v-if="visit.mandator && visit.mandator.delayBetweenVisits !== null"
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-800"
                  >
                    {{ visit.mandator.delayBetweenVisits }} mois
                  </span>
                  <span v-else class="text-gray-400 italic text-xs">Non défini</span>
                </td>

                <!-- ── Dernier occupant ── -->
                <td class="px-4 py-3 align-top">
                  <template v-if="visit.lastOccupant">
                    <div class="font-medium text-gray-800 text-xs">{{ visit.lastOccupant.name }}</div>
                    <div class="mt-0.5 space-y-0.5">
                      <div v-if="visit.lastOccupant.enteredAt" class="text-xs text-gray-500">
                        <span class="text-gray-400" style="font-size:10px">Entrée :</span>
                        {{ formatDate(visit.lastOccupant.enteredAt) }}
                      </div>
                      <div v-if="visit.lastOccupant.resiliedAt">
                        <span
                          class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-orange-100 text-orange-700"
                        >
                          <i class="fas fa-sign-out-alt" style="font-size:9px"></i>
                          Résil. {{ formatDate(visit.lastOccupant.resiliedAt) }}
                        </span>
                      </div>
                      <div v-else>
                        <span class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">
                          <i class="fas fa-home" style="font-size:9px"></i>
                          En place
                        </span>
                      </div>
                    </div>
                  </template>
                  <span v-else class="text-gray-400 italic text-xs">Aucun</span>
                </td>

                <!-- ── Occupation ── -->
                <td class="px-4 py-3 align-top">
                  <div class="space-y-1">
                    <!-- occupancy -->
                    <template v-if="visit.property && visit.property.occupancy">
                      <span
                        :class="visit.property.occupancy === 'OCCUPIED'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600'"
                        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold"
                      >
                        {{ visit.property.occupancy === 'OCCUPIED' ? '🏠 Occupé' : '🔓 Vacant' }}
                      </span>
                    </template>
                    <span v-else class="text-gray-400 italic text-xs">—</span>
                    <!-- scheduledVisitStatus -->
                    <div v-if="visit.property && visit.property.scheduledVisitStatus" class="text-xs">
                      <span class="inline-flex items-center px-2 py-0.5 rounded bg-purple-100 text-purple-700 font-medium">
                        {{ visit.property.scheduledVisitStatus }}
                      </span>
                    </div>
                  </div>
                </td>

                <!-- ── Visitable ── -->
                <td class="px-4 py-3 align-top">
                  <div class="space-y-1">
                    <!-- Assistance -->
                    <div v-if="visit.assistance" class="text-xs">
                      <span class="text-gray-400 uppercase tracking-wide" style="font-size:9px">CAT</span>
                      <span
                        :class="visit.assistance.isVisitable ? 'text-green-700' : 'text-red-600'"
                        class="ml-1 font-semibold"
                      >
                        {{ visit.assistance.isVisitable ? '✅' : '❌' }}
                      </span>
                      <div v-if="visit.assistance.endedAt" class="text-orange-500 mt-0.5" style="font-size:10px">
                        Fin {{ formatDate(visit.assistance.endedAt) }}
                      </div>
                    </div>
                    <!-- Manager -->
                    <div v-if="visit.manager" class="text-xs">
                      <span class="text-gray-400 uppercase tracking-wide" style="font-size:9px">GPR</span>
                      <span
                        :class="visit.manager.isVisitable ? 'text-green-700' : 'text-red-600'"
                        class="ml-1 font-semibold"
                      >
                        {{ visit.manager.isVisitable ? '✅' : '❌' }}
                      </span>
                      <div v-if="visit.manager.endedAt" class="text-orange-500 mt-0.5" style="font-size:10px">
                        Fin {{ formatDate(visit.manager.endedAt) }}
                      </div>
                    </div>
                    <span v-if="!visit.assistance && !visit.manager" class="text-gray-400 italic text-xs">—</span>
                  </div>
                </td>

                <!-- ── Fin gestion ── -->
                <td class="px-4 py-3 align-top whitespace-nowrap">
                  <template v-if="visit.property && visit.property.managementEndedAt">
                    <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">
                      <i class="fas fa-ban mr-1" style="font-size:9px"></i>
                      {{ formatDate(visit.property.managementEndedAt) }}
                    </span>
                  </template>
                  <span v-else class="text-gray-400 italic text-xs">—</span>
                </td>

                <!-- Prochaine visite -->
                <td class="px-4 py-3 whitespace-nowrap">
                  <template v-if="visit.property">
                    <!-- Affichage (caché pendant l'édition) -->
                    <div v-if="editingPropertyId !== visit.property.id">
                      <template v-if="visit.property.scheduledVisitAt">
                        <span :class="dateClass(visit.property.scheduledVisitAt, visit.createdAt, visit.mandator && visit.mandator.delayBetweenVisits)">
                          {{ formatDate(visit.property.scheduledVisitAt) }}
                        </span>
                        <div
                          v-if="isDateMismatch(visit.property.scheduledVisitAt, visit.createdAt, visit.mandator && visit.mandator.delayBetweenVisits)"
                          class="text-xs text-orange-400 mt-0.5"
                        >
                          ⚠ Attendu : {{ formatDate(expectedDate(visit.createdAt, visit.mandator.delayBetweenVisits)) }}
                        </div>
                      </template>
                      <span v-else class="text-gray-400 italic text-xs">Non planifiée</span>
                    </div>

                    <!-- Formulaire d'édition inline -->
                    <div v-else class="flex items-center gap-2 flex-wrap">
                      <input
                        v-model="editingDate"
                        type="date"
                        class="block w-36 px-2 py-1 text-sm border border-indigo-400 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      <button
                        @click="saveScheduledVisit(visit)"
                        :disabled="saving"
                        class="inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
                      >
                        <i :class="saving ? 'fas fa-spinner fa-spin' : 'fas fa-check'" class="mr-1"></i>
                        {{ saving ? '…' : 'Enregistrer' }}
                      </button>
                      <button
                        @click="cancelEdit"
                        class="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                      >
                        Annuler
                      </button>
                    </div>
                  </template>
                  <span v-else class="text-gray-400 italic text-xs">—</span>
                </td>

                <!-- Colonne Diagnostic / Résolution -->
                <td class="px-4 py-3">
                  <template v-if="visit.property">

                    <!-- ─── Diagnostic issu de l'API (ScheduledVisitDiagnosticService) ─── -->
                    <template v-if="visit.diagnostic">

                      <!-- Badge correct / incorrect -->
                      <span
                        :class="visit.diagnostic.isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'"
                        class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold mb-1 whitespace-nowrap"
                      >
                        {{ visit.diagnostic.isCorrect ? '✅ Correct' : '❌ Incorrect' }}
                      </span>

                      <!-- Règle appliquée -->
                      <div class="text-xs mt-1">
                        <span class="text-gray-400 uppercase tracking-wide" style="font-size:10px">Règle : </span>
                        <span v-if="visit.diagnostic.matchedStep" class="font-semibold text-indigo-700">
                          {{ visit.diagnostic.matchedStep.label }}
                        </span>
                        <span v-else class="font-semibold text-red-500">Aucune règle applicable</span>
                      </div>

                      <!-- Date attendue vs stockée (seulement si incorrect) -->
                      <div v-if="!visit.diagnostic.isCorrect" class="mt-1 space-y-0.5 text-xs">
                        <div>
                          <span class="text-gray-400 uppercase tracking-wide" style="font-size:10px">Attendu : </span>
                          <span class="font-semibold text-green-700">
                            {{ visit.diagnostic.expectedScheduledVisitAt ? formatDate(visit.diagnostic.expectedScheduledVisitAt) : 'null' }}
                          </span>
                        </div>
                        <div>
                          <span class="text-gray-400 uppercase tracking-wide" style="font-size:10px">Stocké : </span>
                          <span class="font-semibold text-red-600">
                            {{ visit.diagnostic.currentScheduledVisitAt ? formatDate(visit.diagnostic.currentScheduledVisitAt) : 'null' }}
                          </span>
                        </div>
                      </div>

                      <!-- Raison de l'incorrection -->
                      <div v-if="visit.diagnostic.reason" class="text-xs text-orange-600 mt-1 italic leading-snug">
                        {{ visit.diagnostic.reason }}
                      </div>

                      <!-- Liste des règles évaluées (détail collapsible) -->
                      <details v-if="visit.diagnostic.allSteps && visit.diagnostic.allSteps.length" class="mt-2">
                        <summary class="cursor-pointer text-xs text-gray-400 hover:text-indigo-500 select-none">
                          Toutes les règles ({{ visit.diagnostic.allSteps.length }})
                        </summary>
                        <ul class="mt-1 space-y-0.5">
                          <li
                            v-for="step in visit.diagnostic.allSteps"
                            :key="step.class"
                            :class="[
                              'flex items-center gap-1 text-xs',
                              step.matched ? 'text-indigo-700 font-semibold' :
                              step.skipped ? 'text-gray-300' :
                              step.supports ? 'text-gray-500' : 'text-gray-300'
                            ]"
                          >
                            <span class="w-3 text-center flex-shrink-0">
                              {{ step.matched ? '▶' : step.skipped ? '—' : step.supports ? '✓' : '✗' }}
                            </span>
                            {{ step.label }}
                            <span class="ml-auto text-gray-300 flex-shrink-0" style="font-size:9px">p{{ step.priority }}</span>
                          </li>
                        </ul>
                      </details>

                    </template>

                    <!-- ─── Fallback si diagnostic absent ─── -->
                    <template v-else>
                      <span :class="diagBadgeClass(visit)" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold mb-1 whitespace-nowrap">
                        {{ diagStatus(visit) }}
                      </span>
                      <div class="text-xs text-gray-400 italic mt-1">Diagnostic non disponible</div>
                    </template>

                  </template>
                  <span v-else class="text-gray-400 italic text-xs">—</span>
                </td>

                <!-- Actions -->
                <td class="px-4 py-3">
                  <div v-if="visit.property && editingPropertyId !== visit.property.id" class="flex flex-col gap-1.5">
                    <!-- Modifier manuellement la date -->
                    <button
                      @click="startEdit(visit)"
                      class="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded text-white bg-indigo-500 hover:bg-indigo-600 focus:outline-none transition-colors"
                    >
                      <i class="fas fa-pencil-alt mr-1.5"></i>
                      Modifier
                    </button>
                    <!-- Recalculer via ScheduledVisitChain -->
                    <button
                      @click="recalculate(visit)"
                      :disabled="recalculatingId === visit.property.id"
                      class="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded text-white bg-amber-500 hover:bg-amber-600 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <i :class="recalculatingId === visit.property.id ? 'fas fa-spinner fa-spin' : 'fas fa-calculator'" class="mr-1.5"></i>
                      {{ recalculatingId === visit.property.id ? '…' : 'Recalculer' }}
                    </button>
                  </div>
                </td>
              </tr>

              <!-- Ligne vide -->
              <tr v-if="visits.length === 0 && !loading">
                <td colspan="12" class="text-center py-12 text-gray-400">
                  Aucune visite trouvée.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Indicateur de chargement scroll infini -->
      <div v-if="loading && visits.length > 0" class="text-center py-6">
        <i class="fas fa-spinner fa-spin text-indigo-400 text-2xl"></i>
        <p class="text-sm text-gray-500 mt-2">Chargement de la suite…</p>
      </div>

      <!-- Fin de liste -->
      <div v-if="!hasMore && visits.length > 0" class="text-center py-4 text-xs text-gray-400">
        ✓ Toutes les visites sont affichées ({{ visits.length }})
      </div>

      <!-- Sentinel pour IntersectionObserver -->
      <div ref="scrollSentinel" style="height:1px;"></div>
    </div>

  </div>
</template>

<script>
import api from '@/services/api'
import moment from 'moment'

const LIMIT = 30
const RECALC_BATCH = 100

export default {
  name: 'AdminDernieresVisites',

  data() {
    return {
      visits: [],
      loading: false,
      error: null,

      // Pagination tableau
      currentPage: 0,
      hasMore: true,
      totalCount: 0,

      // Édition inline
      editingPropertyId: null,
      editingDate: '',
      saving: false,

      // Recalcul individuel via ScheduledVisitChain
      recalculatingId: null,

      // Recherche NSIL
      nsilSearch: '',
      searchDebounceTimer: null,

      // Filtre mandataire
      mandatorSearch: '',
      mandatorSearchTimer: null,

      // Flash
      flashMessage: null,
      flashType: 'success',

      // IntersectionObserver
      observer: null,

      // ── Modale de recalcul en batch ──────────────────────────────────────
      recalcModal: {
        open: false,
        phase: 'confirm',  // 'confirm' | 'running' | 'done'
        batchSize: RECALC_BATCH,
        // Progression
        grandTotal: 0,
        processedTotal: 0,
        changedTotal: 0,
        currentPage: 0,
        // Résultats
        errorCount: 0,
        errors: [],
      },
    }
  },

  computed: {
    flashClass() {
      return this.flashType === 'success'
        ? 'bg-green-50 border border-green-200 text-green-800'
        : 'bg-red-50 border border-red-200 text-red-800'
    },
    flashIconClass() {
      return this.flashType === 'success'
        ? 'fas fa-check-circle text-green-500'
        : 'fas fa-times-circle text-red-500'
    },
    progressPercent() {
      if (this.recalcModal.grandTotal <= 0) return 0
      return Math.min(100, Math.round((this.recalcModal.processedTotal / this.recalcModal.grandTotal) * 100))
    },
  },

  watch: {
    nsilSearch() {
      this.onNsilSearchChange()
    },
    mandatorSearch() {
      this.onMandatorSearchChange()
    },
  },

  mounted() {
    this.loadMore()
    this.setupObserver()
  },

  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect()
    }
  },

  methods: {
    // ── Infinite scroll ──────────────────────────────────────────────────────

    setupObserver() {
      if (!window.IntersectionObserver) return
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.loading && this.hasMore) {
              this.loadMore()
            }
          })
        },
        { rootMargin: '300px' }
      )
      if (this.$refs.scrollSentinel) {
        this.observer.observe(this.$refs.scrollSentinel)
      }
    },

    async loadMore() {
      if (this.loading || !this.hasMore) return
      this.loading = true
      this.error = null

      try {
        const nextPage = this.currentPage + 1
        const response = await api.getAllPropertiesByLastVisit(nextPage, LIMIT, this.nsilSearch || null, this.mandatorSearch || null)

        if (!response.success) {
          throw new Error(response.message || 'Erreur inconnue')
        }

        this.visits = [...this.visits, ...(response.data || []).map(this.normalizeItem)]
        this.currentPage = response.meta.page
        this.hasMore = response.meta.hasMore
        this.totalCount = response.meta.total
      } catch (err) {
        this.error = err.message || 'Impossible de charger les visites'
      } finally {
        this.loading = false

        /*
         * IntersectionObserver ne se redéclenche que sur changement d'état.
         * Si le sentinel est encore visible après ce batch (contenu insuffisant),
         * on relance automatiquement le chargement suivant.
         */
        if (this.hasMore) {
          await this.$nextTick()
          const sentinel = this.$refs.scrollSentinel
          if (sentinel) {
            const rect = sentinel.getBoundingClientRect()
            if (rect.top < window.innerHeight + 300) {
              setTimeout(() => this.loadMore(), 50)
            }
          }
        }
      }
    },

    /**
     * Normalise un item retourné par l'API /admin/properties/incorrect-scheduled-visit
     * (centré sur la propriété) vers la structure visit-centric attendue par le template.
     */
    normalizeItem(item) {
      // Préférence : mandataire assistance si délai défini, sinon gérant, sinon assistance sans délai
      const mdt = (item.assistance && item.assistance.delayBetweenVisits !== null)
        ? item.assistance
        : (item.manager || item.assistance || null)
      return {
        id: item.id,
        createdAt: item.lastVisit ? item.lastVisit.createdAt : null,
        property: {
          id: item.id,
          nsil: item.nsil,
          propertyCode: item.propertyCode,
          occupancy: item.occupancy || null,
          scheduledVisitAt: item.scheduledVisitAt,
          scheduledVisitStatus: item.scheduledVisitStatus || null,
          nextComputationAt: item.nextComputationAt,
          managementEndedAt: item.managementEndedAt || null,
          dataRefreshedAt: item.dataRefreshedAt,
          altaixUpdatedAt: item.altaixUpdatedAt || null,
          addressStreet: item.address ? item.address.street : null,
          addressZipCode: item.address ? item.address.zipCode : null,
          addressCity: item.address ? item.address.city : null,
        },
        mandator: mdt ? {
          name: mdt.mandatorName,
          delayBetweenVisits: mdt.delayBetweenVisits !== undefined ? mdt.delayBetweenVisits : null,
        } : null,
        // Données brutes des mandataires pour les colonnes de détail
        assistance: item.assistance ? {
          isVisitable: item.assistance.isVisitable,
          endedAt: item.assistance.endedAt || null,
          mandatorName: item.assistance.mandatorName || null,
          delayBetweenVisits: item.assistance.delayBetweenVisits,
        } : null,
        manager: item.manager ? {
          isVisitable: item.manager.isVisitable,
          endedAt: item.manager.endedAt || null,
          mandatorName: item.manager.mandatorName || null,
          delayBetweenVisits: item.manager.delayBetweenVisits,
        } : null,
        // Dernier occupant
        lastOccupant: item.lastOccupant ? {
          name: item.lastOccupant.name,
          enteredAt: item.lastOccupant.enteredAt || null,
          resiliedAt: item.lastOccupant.resiliedAt || null,
        } : null,
        diagnostic: item.diagnostic || null,
      }
    },

    resetAndLoad() {
      this.visits = []
      this.currentPage = 0
      this.hasMore = true
      this.totalCount = 0
      this.error = null
      this.cancelEdit()
      this.loadMore()
    },

    onNsilSearchChange() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => {
        this.visits = []
        this.currentPage = 0
        this.hasMore = true
        this.totalCount = 0
        this.error = null
        this.loadMore()
      }, 400)
    },

    onMandatorSearchChange() {
      if (this.mandatorSearchTimer) clearTimeout(this.mandatorSearchTimer)
      this.mandatorSearchTimer = setTimeout(() => {
        this.visits = []
        this.currentPage = 0
        this.hasMore = true
        this.totalCount = 0
        this.error = null
        this.loadMore()
      }, 400)
    },

    // ── Modale de recalcul en batch ──────────────────────────────────────────

    /** Ouvre la modale en phase "confirmation". */
    openRecalcModal() {
      this.recalcModal.open = true
      this.recalcModal.phase = 'confirm'
      this.recalcModal.grandTotal = 0
      this.recalcModal.processedTotal = 0
      this.recalcModal.changedTotal = 0
      this.recalcModal.currentPage = 0
      this.recalcModal.errorCount = 0
      this.recalcModal.errors = []
    },

    /** Ferme la modale (uniquement si on n'est pas en train de traiter). */
    closeRecalcModal() {
      if (this.recalcModal.phase === 'running') return
      this.recalcModal.open = false
    },

    /** Ferme la modale en cliquant sur l'overlay (uniquement en phase "done"). */
    closeRecalcModalIfDone() {
      if (this.recalcModal.phase === 'done') {
        this.closeRecalcModalAndRefresh()
      }
    },

    /** Ferme la modale et recharge le tableau. */
    closeRecalcModalAndRefresh() {
      this.recalcModal.open = false
      this.resetAndLoad()
    },

    /**
     * Lance le recalcul par batch successifs.
     * Chaque appel API traite RECALC_BATCH lots, puis on enchaîne automatiquement
     * jusqu'à ce que hasMore soit false.
     */
    async startRecalculation() {
      this.recalcModal.phase = 'running'
      this.recalcModal.processedTotal = 0
      this.recalcModal.changedTotal = 0
      this.recalcModal.errorCount = 0
      this.recalcModal.errors = []
      this.recalcModal.currentPage = 0
      this.recalcModal.grandTotal = 0

      let page = 1
      let hasMore = true

      while (hasMore) {
        this.recalcModal.currentPage = page

        try {
          const response = await api.recalculateAllIncorrect(page, RECALC_BATCH) // eslint-disable-line no-await-in-loop

          if (!response.success) {
            throw new Error(response.message || 'Erreur lors du recalcul')
          }

          // Mettre à jour le total si c'est la première page
          if (page === 1) {
            this.recalcModal.grandTotal = response.total || 0
          }

          this.recalcModal.processedTotal += response.processed || 0
          this.recalcModal.changedTotal += response.changed || 0

          if (response.errors && response.errors.length) {
            this.recalcModal.errorCount += response.errors.length
            this.recalcModal.errors.push(...response.errors)
          }

          hasMore = response.hasMore === true
          page++
        } catch (err) {
          // En cas d'erreur réseau, on stoppe la boucle et on informe
          this.recalcModal.errorCount++
          this.recalcModal.errors.push({
            id: `batch-page-${page}`,
            nsil: `Batch page ${page}`,
            message: err.message || 'Erreur réseau inconnue',
          })
          hasMore = false
        }
      }

      // Assurer que la barre est bien à 100% à la fin
      if (this.recalcModal.grandTotal > 0) {
        this.recalcModal.processedTotal = this.recalcModal.grandTotal
      }

      this.recalcModal.phase = 'done'
    },

    // ── Édition inline ───────────────────────────────────────────────────────

    startEdit(visit) {
      this.editingPropertyId = visit.property.id
      this.editingDate = visit.property.scheduledVisitAt
        ? visit.property.scheduledVisitAt.slice(0, 10)
        : ''
    },

    cancelEdit() {
      this.editingPropertyId = null
      this.editingDate = ''
    },

    async saveScheduledVisit(visit) {
      if (!this.editingDate) {
        this.showFlash('Veuillez sélectionner une date.', 'error')
        return
      }

      this.saving = true
      try {
        const response = await api.updateAdminScheduledVisit(visit.property.id, this.editingDate)

        if (response.success) {
          visit.property.scheduledVisitAt = response.scheduledVisitAt || this.editingDate
          this.cancelEdit()
          this.showFlash(
            `Date de prochaine visite du lot ${visit.property.nsil || visit.property.propertyCode || ''} mise à jour au ${this.formatDate(response.scheduledVisitAt || this.editingDate)}.`,
            'success'
          )
        } else {
          throw new Error(response.message || 'Erreur lors de la mise à jour')
        }
      } catch (err) {
        this.showFlash(err.message || 'Impossible de mettre à jour la date.', 'error')
      } finally {
        this.saving = false
      }
    },

    // ── Recalcul via ScheduledVisitChain ─────────────────────────────────────

    /**
     * Déclenche le recalcul de la date de prochaine visite via l'API,
     * puis met à jour la ligne dans le tableau (scheduledVisitAt + diagnostic).
     */
    async recalculate(visit) {
      if (!visit.property) return
      this.recalculatingId = visit.property.id

      try {
        const response = await api.recalculateAdminScheduledVisit(visit.property.id)

        if (!response.success) {
          throw new Error(response.message || 'Erreur lors du recalcul')
        }

        // Mise à jour de la ligne sans rechargement complet
        visit.property.scheduledVisitAt = response.scheduledVisitAt || null
        visit.property.nextComputationAt = response.nextComputationAt || visit.property.nextComputationAt
        visit.property.dataRefreshedAt = response.dataRefreshedAt || visit.property.dataRefreshedAt
        if (response.diagnostic) {
          visit.diagnostic = response.diagnostic
        }

        const nsil = visit.property.nsil || visit.property.propertyCode || ''
        this.showFlash(
          `Lot ${nsil} recalculé → ${response.scheduledVisitAt ? this.formatDate(response.scheduledVisitAt) : 'null'}`,
          'success'
        )
      } catch (err) {
        this.showFlash(err.message || 'Impossible de recalculer la date.', 'error')
      } finally {
        this.recalculatingId = null
      }
    },

    // ── Diagnostic ───────────────────────────────────────────────────────────

    /**
     * Statut principal du diagnostic :
     *  🔴 Date non calculée  – scheduledVisitAt null ET délai défini
     *  ⚠️ Délai non défini   – scheduledVisitAt null ET pas de délai
     *  ⚠️ next_computation_at manquant – date présente mais nextComputationAt null
     *  ⚠️ Jamais synchronisé – dataRefreshedAt null
     *  ✅ OK
     */
    diagStatus(visit) {
      const p = visit.property
      const m = visit.mandator
      const delay = m ? m.delayBetweenVisits : null
      const sched = p ? p.scheduledVisitAt : null
      const dataRef = p ? p.dataRefreshedAt : null

      if (!sched && delay !== null && delay !== undefined) return '🔴 Date non calculée'
      if (!sched) return '⚠️ Délai non défini'
      if (!dataRef) return '⚠️ Jamais synchronisé PWA'
      return '✅ OK'
    },

    diagBadgeClass(visit) {
      const status = this.diagStatus(visit)
      if (status.startsWith('🔴')) return 'bg-red-100 text-red-700'
      if (status.startsWith('⚠️')) return 'bg-yellow-100 text-yellow-700'
      return 'bg-green-100 text-green-700'
    },

    // ── Helpers dates ────────────────────────────────────────────────────────

    expectedDate(visitCreatedAt, delay) {
      if (!visitCreatedAt || !delay) return null
      return moment(visitCreatedAt).add(delay, 'months').format('YYYY-MM-DD')
    },

    dateClass(scheduledStr, visitCreatedAt, delay) {
      if (!scheduledStr) return 'text-gray-400 italic text-xs'
      const today = moment().format('YYYY-MM-DD')
      if (scheduledStr < today) return 'text-red-600 font-semibold text-sm'
      if (!delay) return 'text-blue-600 font-semibold text-sm'
      const expected = moment(visitCreatedAt).add(delay, 'months')
      const scheduled = moment(scheduledStr)
      const diffDays = Math.abs(scheduled.diff(expected, 'days'))
      if (diffDays <= 15) return 'text-green-700 font-semibold text-sm'
      return 'text-orange-500 font-semibold text-sm'
    },

    isDateMismatch(scheduledStr, visitCreatedAt, delay) {
      if (!scheduledStr || !delay || !visitCreatedAt) return false
      const today = moment().format('YYYY-MM-DD')
      if (scheduledStr < today) return false
      const expected = moment(visitCreatedAt).add(delay, 'months')
      const scheduled = moment(scheduledStr)
      return Math.abs(scheduled.diff(expected, 'days')) > 15
    },

    formatDate(dateStr) {
      if (!dateStr) return '—'
      return moment(dateStr).format('DD/MM/YYYY')
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '—'
      return moment(dateStr).format('DD/MM/YYYY HH:mm')
    },

    formatAddress(property) {
      const parts = []
      if (property.addressStreet) parts.push(property.addressStreet)
      if (property.addressZipCode) parts.push(property.addressZipCode)
      if (property.addressCity) parts.push(property.addressCity)
      return parts.join(', ') || ''
    },

    showFlash(message, type = 'success') {
      this.flashMessage = message
      this.flashType = type
      setTimeout(() => {
        if (this.flashMessage === message) this.flashMessage = null
      }, 5000)
    },
  },
}
</script>

<style scoped>
/* Flash message */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Modale de recalcul */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
.modal-fade-enter-active > div,
.modal-fade-leave-active > div {
  transition: transform 0.2s ease;
}
.modal-fade-enter-from > div,
.modal-fade-leave-to > div {
  transform: scale(0.95) translateY(-8px);
}
</style>
